import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, Users, ArrowUpDown, Calculator, Sparkles } from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import { getLeadScores, refreshLeadScores } from '../../services/api';

interface LeadScoreTableProps {
    projectId: string;
}

interface ScoreBreakdown {
    method?: string;
    probability?: number;
    top_factors?: string[];
    reasoning?: string;
}

interface ModelMeta {
    n_samples?: number;
    n_holdout?: number;
    holdout_accuracy?: number;
    baseline_accuracy?: number;
    features?: string[];
}

interface LeadScore {
    lead_id: string;
    email: string;
    score: number;
    conversion_probability: number;
    recommended_action: string;
    last_updated: string;
    factors?: string[];
    score_breakdown?: ScoreBreakdown;
}

interface LeadScoresResponse {
    leads: LeadScore[];
    total: number;
    project_id: string;
    generated_at: string;
    method?: string;
    model_meta?: ModelMeta;
    reason?: string;
}

interface ScoreRefreshMeta {
    method?: string;
    model_meta?: ModelMeta;
    reason?: string;
}

const HEURISTIC_REASON_TEXT: Record<string, string> = {
    insufficient_labeled_data: 'Not enough labeled leads yet — using AI heuristic scoring',
    model_below_baseline: 'Trained model scored below baseline — using AI heuristic scoring',
};

type SortField = 'email' | 'score' | 'conversion_probability' | 'recommended_action' | 'last_updated';
type SortDir = 'asc' | 'desc';

const ACTION_BADGES: Record<string, { label: string; variant: 'danger' | 'info' | 'default' | 'warning' }> = {
    hot_follow_up: { label: 'Hot Follow-up', variant: 'danger' },
    nurture: { label: 'Nurture', variant: 'info' },
    cold: { label: 'Cold', variant: 'default' },
    disqualified: { label: 'Disqualified', variant: 'warning' },
};

function getScoreColor(score: number): string {
    if (score >= 61) return 'var(--success)';
    if (score >= 31) return 'var(--warning)';
    return 'var(--danger)';
}

function getScoreClass(score: number): string {
    if (score >= 61) return 'lead-score-high';
    if (score >= 31) return 'lead-score-mid';
    return 'lead-score-low';
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '\u2014';
    try {
        return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
        return iso;
    }
}

const LeadScoreTable: React.FC<LeadScoreTableProps> = ({ projectId }) => {
    const [data, setData] = useState<LeadScoresResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState<SortField>('score');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [refreshMeta, setRefreshMeta] = useState<ScoreRefreshMeta | null>(null);

    const fetchScores = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getLeadScores(projectId, 50);
            setData(result);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load lead scores');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchScores();
    }, [fetchScores]);

    const handleRefresh = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const refreshResult = await refreshLeadScores(projectId);
            if (refreshResult && (refreshResult.method || refreshResult.model_meta || refreshResult.reason)) {
                setRefreshMeta({
                    method: refreshResult.method,
                    model_meta: refreshResult.model_meta,
                    reason: refreshResult.reason,
                });
            }
            await fetchScores();
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to refresh lead scores');
        } finally {
            setRefreshing(false);
        }
    };

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    const sortedLeads = data?.leads ? [...data.leads].sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
            case 'email': cmp = a.email.localeCompare(b.email); break;
            case 'score': cmp = a.score - b.score; break;
            case 'conversion_probability': cmp = a.conversion_probability - b.conversion_probability; break;
            case 'recommended_action': cmp = a.recommended_action.localeCompare(b.recommended_action); break;
            case 'last_updated': cmp = new Date(a.last_updated || 0).getTime() - new Date(b.last_updated || 0).getTime(); break;
        }
        return sortDir === 'asc' ? cmp : -cmp;
    }) : [];

    const getActionBadge = (action: string) => {
        const config = ACTION_BADGES[action] || { label: action.replace(/_/g, ' '), variant: 'default' as const };
        return <GlassBadge variant={config.variant} size="sm">{config.label}</GlassBadge>;
    };

    // Scoring-method transparency (additive fields — absent on legacy payloads)
    const modelMeta = refreshMeta?.model_meta ?? data?.model_meta;
    const heuristicReason = refreshMeta?.reason ?? data?.reason;
    let methodNote: { icon: React.ReactNode; text: string } | null = null;
    if (modelMeta) {
        let text = 'Scored by trained model';
        if (typeof modelMeta.holdout_accuracy === 'number' && typeof modelMeta.baseline_accuracy === 'number') {
            text += ` — holdout accuracy ${Math.round(modelMeta.holdout_accuracy * 100)}% vs ${Math.round(modelMeta.baseline_accuracy * 100)}% baseline`;
        }
        if (typeof modelMeta.n_samples === 'number') {
            text += `, n=${modelMeta.n_samples}`;
        }
        methodNote = { icon: <Calculator size={14} style={{ color: 'var(--success)', flexShrink: 0 }} />, text };
    } else if (heuristicReason) {
        methodNote = {
            icon: <Sparkles size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />,
            text: HEURISTIC_REASON_TEXT[heuristicReason] || 'Using AI heuristic scoring',
        };
    }

    return (
        <div className="lead-score-section">
            {/* Header */}
            <GlassCard padding="md" className="lead-score-header-card">
                <div className="lead-score-header-row">
                    <div className="lead-score-header-info">
                        <Users size={20} style={{ color: 'var(--primary-500)' }} />
                        <div>
                            <h3 style={{ margin: 0, color: 'var(--text)', fontWeight: 'var(--font-medium)' }}>
                                Lead Scores
                            </h3>
                            {data && (
                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                    {data.total} leads scored
                                </span>
                            )}
                            {methodNote && (
                                <div className="lead-score-method-note">
                                    {methodNote.icon}
                                    <span>{methodNote.text}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <GlassButton
                        variant="primary"
                        size="sm"
                        icon={<RefreshCw size={14} />}
                        onClick={handleRefresh}
                        loading={refreshing}
                    >
                        Refresh Scores
                    </GlassButton>
                </div>
            </GlassCard>

            {/* Loading */}
            {loading && !data && (
                <GlassCard padding="lg">
                    <div className="lead-score-loading">
                        <RefreshCw size={24} className="trend-spinner" style={{ color: 'var(--primary-500)' }} />
                        <p style={{ color: 'var(--text-secondary)', margin: '12px 0 0' }}>
                            Loading lead scores...
                        </p>
                    </div>
                </GlassCard>
            )}

            {/* Error */}
            {error && (
                <GlassCard padding="md">
                    <div className="trend-error">
                        <AlertCircle size={20} style={{ color: 'var(--danger)' }} />
                        <span style={{ color: 'var(--danger)' }}>{error}</span>
                    </div>
                </GlassCard>
            )}

            {/* Table */}
            {data && !loading && (
                <GlassCard padding="none" className="lead-score-table-card">
                    <div className="lead-score-table-wrapper">
                        <table className="lead-score-table">
                            <thead>
                                <tr>
                                    <th onClick={() => toggleSort('email')} className="lead-score-th sortable">
                                        <span>Email</span>
                                        {sortField === 'email' && <ArrowUpDown size={12} />}
                                    </th>
                                    <th onClick={() => toggleSort('score')} className="lead-score-th sortable">
                                        <span>Score</span>
                                        {sortField === 'score' && <ArrowUpDown size={12} />}
                                    </th>
                                    <th onClick={() => toggleSort('conversion_probability')} className="lead-score-th sortable">
                                        <span>Conversion</span>
                                        {sortField === 'conversion_probability' && <ArrowUpDown size={12} />}
                                    </th>
                                    <th onClick={() => toggleSort('recommended_action')} className="lead-score-th sortable">
                                        <span>Action</span>
                                        {sortField === 'recommended_action' && <ArrowUpDown size={12} />}
                                    </th>
                                    <th onClick={() => toggleSort('last_updated')} className="lead-score-th sortable">
                                        <span>Last Updated</span>
                                        {sortField === 'last_updated' && <ArrowUpDown size={12} />}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedLeads.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="lead-score-empty">
                                            No scored leads yet. Click "Refresh Scores" to generate.
                                        </td>
                                    </tr>
                                ) : (
                                    sortedLeads.map((lead) => (
                                        <tr key={lead.lead_id} className="lead-score-row">
                                            <td className="lead-score-td lead-score-email">{lead.email}</td>
                                            <td className="lead-score-td">
                                                <div className="lead-score-bar-container">
                                                    <div
                                                        className={`lead-score-bar ${getScoreClass(lead.score)}`}
                                                        style={{ width: `${lead.score}%`, backgroundColor: getScoreColor(lead.score) }}
                                                    />
                                                    <span className="lead-score-value">{lead.score}</span>
                                                </div>
                                            </td>
                                            <td className="lead-score-td">
                                                {Math.round(lead.conversion_probability * 100)}%
                                            </td>
                                            <td className="lead-score-td">
                                                {getActionBadge(lead.recommended_action)}
                                            </td>
                                            <td className="lead-score-td lead-score-date">
                                                {formatDate(lead.last_updated)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </GlassCard>
            )}
        </div>
    );
};

export default LeadScoreTable;
